"use client";

import { useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { api, useApi } from "@/lib/useApi";
import { Button, ErrorBox, Notice } from "../ui";
import { SectionHead } from "./SettingsTable";
import { useDialog } from "@/components/Dialog";

interface Domain {
  host: string;
  verifiedAt: number | null;
  problem: string | null;
}

const box = "h-6 w-72 max-w-full rounded-md border border-line bg-surface-2 px-1.5 text-[12px]";

/**
 * The installation's domain (administrators): the name, whether it reaches this installation over HTTPS, and why not.
 * The web server fetches the certificate by itself once the name is stored and points here.
 */
export function DomainPanel() {
  const { t } = useI18n();
  const dialog = useDialog();
  const { data, reload } = useApi<{ domain: Domain | null }>("/api/domain");
  const [host, setHost] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // one request (the check can take a while: the certificate is fetched), then the state again
  const run = async (path: string, init: Parameters<typeof api>[1]) => {
    setBusy(true);
    setError(null);
    try {
      await api(path, init);
      setHost("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      reload();
    }
  };
  const apply = (e: React.FormEvent) => {
    e.preventDefault();
    void run("/api/domain", { method: "PUT", json: { host } });
  };
  const remove = async (d: Domain) => (await dialog.confirm(t("set.domain.removeConfirm", { host: d.host }), { danger: true })) && void run("/api/domain", { method: "DELETE" });

  if (!data) return <div className="skeleton h-28" />;
  const d = data.domain;
  return (
    <section className="space-y-2">
      <SectionHead title={t("set.nav.domain")} description={t("set.domain.hint")} />
      <DomainState d={d} busy={busy} />
      {error && <ErrorBox>{error}</ErrorBox>}
      <form onSubmit={apply} className="flex flex-wrap items-center gap-1.5 border-t border-line pt-1.5">
        <input aria-label={t("set.domain.host")} className={box} placeholder={t("set.domain.placeholder")} value={host} onChange={(e) => setHost(e.target.value)} required />
        <Button size="xs" variant="primary" disabled={busy || !host.trim()}>{t("set.domain.apply")}</Button>
        {d && <Button type="button" size="xs" onClick={() => void run("/api/domain/verify", { method: "POST" })} disabled={busy}>{t("set.domain.recheck")}</Button>}
        {d && <Button type="button" size="xs" variant="ghost" className="ml-auto" onClick={() => remove(d)} disabled={busy}>{t("set.domain.remove")}</Button>}
      </form>
    </section>
  );
}

/** The domain's state in one line: none, being checked, active, or why it does not reach this installation yet. */
function DomainState({ d, busy }: { d: Domain | null; busy: boolean }) {
  const { t, f } = useI18n();
  if (busy) return <Notice>{t("set.domain.checking")}</Notice>;
  if (!d) return <p className="text-[12px] text-muted">{t("set.domain.none")}</p>;
  if (d.verifiedAt) return <Notice tone="good">✓ {t("set.domain.active", { date: f.date(d.verifiedAt), host: d.host })}</Notice>;
  return (
    <div className="space-y-1">
      <p className="text-[12px]"><span className="font-semibold">{d.host}</span> · <span className={d.problem ? "text-bad-ink" : "text-muted"}>{t(d.problem ? "set.domain.failed" : "set.domain.pending")}</span></p>
      {d.problem && <ErrorBox>{t(d.problem)}</ErrorBox>}
    </div>
  );
}
